import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useFinancialYearStore from "../../store/financialYear";
import SelectVendors from "../../components/ui/selectors/SelectVendors";
import SelectCustomers from "../../components/ui/selectors/SelectCustomers";
import Select from "../../components/ui/Select";
import Input from "../../components/ui/Input";
import { useGenerateAgentSalesReport } from "../../http-hooks/agentSales/useAgentSales";
import { getDocumentViewerRoute } from "../../utils/documentUrlHelper";

// Report type options
const reportTypeOptions = [
  { value: "V", label: "Vendorwise" },
  { value: "C", label: "Customerwise" },
];

// Group options
const groupOptions = [
  { value: "0", label: "No" },
  { value: "1", label: "Yes" },
];

// Report mode options
const reportModeOptions = [
  { value: "detail", label: "Detail" },
  { value: "summary", label: "Summary" },
];

// Year comparison options
const yearCompareOptions = [
  { value: "1", label: "1 Year (CY vs PY)" },
  { value: "2", label: "2 Years (CY vs PY vs PY-2)" },
  { value: "3", label: "3 Years (CY vs PY vs PY-2 vs PY-3)" },
];

// Report on options
const reportOnOptions = [
  { value: "gross", label: "Bill Value (excluding tax)" },
  { value: "bill", label: "Bill Value (including tax)" },
];

// Scheme filter options
const schemeFilterOptions = [
  { value: "all", label: "All Bills" },
  { value: "scheme", label: "Scheme Bills Only" },
  { value: "non_scheme", label: "Non-Scheme Bills Only" },
];

const AgentSalesAnalytics = () => {
  const { getFys, getFye } = useFinancialYearStore();
  const navigate = useNavigate();
  const { mutateAsync: generateReport, isPending: isGenerating } = useGenerateAgentSalesReport();

  // Get financial year dates
  const fys = getFys();
  const fye = getFye();

  const getInitialToDate = () => {
    if (!fye) return "";
    const today = new Date();
    const fyToDate = new Date(fye);
    return today < fyToDate ? today.toISOString().split("T")[0] : fye;
  };

  // Form state
  const [fromDate, setFromDate] = useState(fys || "");
  const [toDate, setToDate] = useState(getInitialToDate);
  const [formType, setFormType] = useState(reportTypeOptions[0]);
  const [vendors, setVendors] = useState([]);
  const [isGroup, setIsGroup] = useState(groupOptions[0]);
  const [customers, setCustomers] = useState([]);
  const [isCustGroup, setIsCustGroup] = useState(groupOptions[0]);
  const [reportMode, setReportMode] = useState(reportModeOptions[0]);
  const [yearCompare, setYearCompare] = useState(yearCompareOptions[0]);
  const [reportOn, setReportOn] = useState(reportOnOptions[0]);
  const [schemeFilter, setSchemeFilter] = useState(schemeFilterOptions[0]);

  const buildPrevYears = (from, to, count) => {
    const years = [];
    for (let i = 1; i <= count; i++) {
      const pFrom = new Date(from);
      pFrom.setFullYear(pFrom.getFullYear() - i);
      const pTo = new Date(to);
      pTo.setFullYear(pTo.getFullYear() - i);
      years.push({
        fromDate: pFrom.toISOString().split("T")[0],
        toDate: pTo.toISOString().split("T")[0],
      });
    }
    return years;
  };

  const prevYears = buildPrevYears(fromDate, toDate, parseInt(yearCompare.value));

  useEffect(() => {
    if (fys && fye) {
      setFromDate(fys);
      const today = new Date();
      const fyToDate = new Date(fye);
      setToDate(today < fyToDate ? today.toISOString().split("T")[0] : fye);
    }
  }, [fys, fye]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid()) {
      return;
    }

    const formData = {
      fromDate,
      toDate,
      prevYears,
      formType: formType.value,
      reportMode: reportMode.value,
      vendors: vendors.map(v => v.ps_Id),
      isGroup: isGroup.value,
      customers: customers.map(c => c.ps_Id),
      isCustGroup: isCustGroup.value,
      reportOn: reportOn.value,
      schemeFilter: schemeFilter.value,
    };

    const data = await generateReport(formData);

    if (data?.filePath) {
      const baseUrl = process.env.REACT_APP_MAIN_BASE_URL || "";
      const htmlUrl = `${baseUrl}${data.filePath}`;
      const reportLabel = formType.value === "V" ? "Vendorwise" : "Customerwise";
      const modeLabel = reportMode.value === "summary" ? "Summary" : "Detail";
      const yearLabel = yearCompare.value + "Y";

      const route = getDocumentViewerRoute("agent-sales-report", {
        htmlUrl,
        pdfUrl: "",
        documentNumber: `${reportLabel} ${modeLabel} ${yearLabel}`,
        documentType: "Sales Comparison",
      });

      navigate(route);
    }
  };

  const isSummary = reportMode.value === "summary";

  const isFormValid = () => {
    if (!fromDate || !toDate) return false;
    
    if (isSummary) return true;

    const hasVendors = Array.isArray(vendors) && vendors.length > 0;
    const hasCustomers = Array.isArray(customers) && customers.length > 0;

    // Detail mode: only the main party is required, counterparty auto-fetches all if empty
    return formType.value === "V" ? hasVendors : hasCustomers;
  };

  if (!fys || !fye) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <p className="text-gray-500">Loading financial year data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold leading-6 text-gray-900">
            Agent Sales Analytics
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Generate agent sales analytics report comparing current and previous financial year
          </p>
        </div>
      </div>

      <div className="mt-8 bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
        <form onSubmit={handleSubmit}>
          <div className="px-4 py-6 sm:p-8">
            <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
              {/* Current FY From Date */}
              <div className="sm:col-span-3">
                <Input
                  type="date"
                  label="From: (Current Year)"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  min={fys}
                  max={fye}
                  isRequired
                />
              </div>

              {/* Current FY To Date */}
              <div className="sm:col-span-3">
                <Input
                  type="date"
                  label="To: (Current Year)"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  min={fys}
                  max={fye}
                  isRequired
                />
              </div>

              {/* Compare Years */}
              <div className="sm:col-span-2">
                <Select
                  options={yearCompareOptions}
                  value={yearCompare}
                  onChange={setYearCompare}
                  label="Compare Years"
                  isRequired
                />
              </div>

              {/* Report Type */}
              <div className="sm:col-span-2">
                <Select
                  options={reportTypeOptions}
                  value={formType}
                  onChange={setFormType}
                  label="Report Type"
                  isRequired
                />
              </div>

              {/* Report Mode */}
              <div className="sm:col-span-2">
                <Select
                  options={reportModeOptions}
                  value={reportMode}
                  onChange={setReportMode}
                  label="Report Mode"
                  isRequired
                />
              </div>

              {/* Previous year periods info */}
              <div className="sm:col-span-6">
                <div className="flex flex-wrap gap-3">
                  {prevYears.map((py, i) => (
                    <div key={i} className="text-xs text-gray-500 bg-gray-50 rounded px-3 py-1.5">
                      <span className="font-medium text-gray-700">PY{i > 0 ? `-${i + 1}` : ""}:</span>{" "}
                      {py.fromDate} to {py.toDate}
                    </div>
                  ))}
                </div>
              </div>

              {/* Vendor Selection - hidden in summary mode */}
              {!isSummary && (
                <div className="sm:col-span-3">
                  <SelectVendors
                    value={vendors}
                    onChange={setVendors}
                    label={formType.value === "V" ? "Vendor *" : "Vendor (optional - all if empty)"}
                    isMulti
                  />
                </div>
              )}

              {/* Vendor Group - hidden in summary mode */}
              {!isSummary && (
                <div className="sm:col-span-3">
                  <Select
                    options={groupOptions}
                    value={isGroup}
                    onChange={setIsGroup}
                    label="Vendor Group"
                  />
                </div>
              )}

              {/* Customer Selection - hidden in summary mode */}
              {!isSummary && (
                <div className="sm:col-span-3">
                  <SelectCustomers
                    value={customers}
                    onChange={setCustomers}
                    label={formType.value === "C" ? "Customer *" : "Customer (optional - all if empty)"}
                    isMulti
                  />
                </div>
              )}

              {/* Customer Group - hidden in summary mode */}
              {!isSummary && (
                <div className="sm:col-span-3">
                  <Select
                    options={groupOptions}
                    value={isCustGroup}
                    onChange={setIsCustGroup}
                    label="Customer Group"
                  />
                </div>
              )}

              {/* Report On */}
              <div className="sm:col-span-3">
                <Select
                  options={reportOnOptions}
                  value={reportOn}
                  onChange={setReportOn}
                  label="Report On"
                  isRequired
                />
              </div>

              {/* Scheme Filter */}
              <div className="sm:col-span-3">
                <Select
                  options={schemeFilterOptions}
                  value={schemeFilter}
                  onChange={setSchemeFilter}
                  label="Bill Type"
                  isRequired
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-center gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
            <button
              type="submit"
              disabled={!isFormValid() || isGenerating}
              className={`rounded-md px-8 py-2.5 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
                isFormValid() && !isGenerating
                  ? "bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600"
                  : "bg-gray-400 cursor-not-allowed"
              }`}
            >
              {isGenerating ? "Generating..." : "Generate Report"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AgentSalesAnalytics;

